import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { BackLink } from "@/components/brand/BackLink";
import { CalculatorDisclaimer } from "@/components/brand/CalculatorDisclaimer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  fmtUSD,
  emailShareLink,
  logCalculatorCompletion,
} from "@/lib/calculators";

export const Route = createFileRoute("/tools/dime")({
  head: () => ({
    meta: [
      { title: "Life Insurance Calculator — The DIME Method | Currency Thoughts" },
      {
        name: "description",
        content:
          "What would happen to your family if you weren't here tomorrow? The DIME method gets you to the real coverage number — Debt, Income, Mortgage, Education.",
      },
      { property: "og:title", content: "The DIME Life Insurance Calculator" },
      {
        property: "og:description",
        content:
          "Most families are dramatically underinsured. Find your real number in 3 steps.",
      },
    ],
    links: [{ rel: "canonical", href: "/tools/dime" }],
  }),
  component: DimePage,
});

interface DimeState {
  debt: number;
  income: number;
  incomeYears: number;
  mortgage: number;
  kids: number;
  educationLevel: "public" | "private" | "premium";
}

const EDU_PER_CHILD: Record<DimeState["educationLevel"], number> = {
  public: 25000,
  private: 100000,
  premium: 250000,
};

const EDU_LABELS: Record<DimeState["educationLevel"], string> = {
  public: "In-state public · ~$25K/child",
  private: "Private 4-yr · ~$100K/child",
  premium: "Top private · ~$250K/child",
};

function computeDime(d: DimeState) {
  const debt = d.debt;
  const incomeReplace = d.income * d.incomeYears;
  const mortgage = d.mortgage;
  const education = d.kids * EDU_PER_CHILD[d.educationLevel];
  return { debt, incomeReplace, mortgage, education, total: debt + incomeReplace + mortgage + education };
}

function DimePage() {
  const [step, setStep] = useState<0 | 1 | 2 | 3>(0);
  const [done, setDone] = useState(false);
  const [d, setD] = useState<DimeState>({
    debt: 50000,
    income: 100000,
    incomeYears: 10,
    mortgage: 250000,
    kids: 2,
    educationLevel: "public",
  });
  const calc = useMemo(() => computeDime(d), [d]);

  // Progressive accumulator (0..1) — reveals as steps complete.
  const filled = step === 0 ? 0 : step / 3;

  async function onComplete() {
    const r = await logCalculatorCompletion("dime", d, calc);
    if (r.saved) toast.success("Saved to your Vault");
    setDone(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 pt-12 pb-24">
        <BackLink to="/" label="Back to home" />

        {!done ? (
          <Intro
            step={step}
            filled={filled}
            d={d}
            setD={setD}
            calc={calc}
            onNext={() => setStep((s) => (s + 1) as 0 | 1 | 2 | 3)}
            onBack={() => setStep((s) => Math.max(0, s - 1) as 0 | 1 | 2 | 3)}
            onSubmit={onComplete}
          />
        ) : (
          <Result d={d} calc={calc} onRestart={() => { setDone(false); setStep(0); }} />
        )}
      </main>
      <Footer />
    </div>
  );
}

/* ---------------- INTRO + STEPS ---------------- */

function Intro({
  step,
  filled,
  d,
  setD,
  calc,
  onNext,
  onBack,
  onSubmit,
}: {
  step: 0 | 1 | 2 | 3;
  filled: number;
  d: DimeState;
  setD: (d: DimeState) => void;
  calc: ReturnType<typeof computeDime>;
  onNext: () => void;
  onBack: () => void;
  onSubmit: () => void;
}) {
  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-12">
      <div>
        <Eyebrow>— Life Insurance Calculator —</Eyebrow>
        <h1 className="mt-5 font-display text-[36px] md:text-[42px] leading-[1.08] text-navy-900">
          What would happen to your family if you weren&rsquo;t here tomorrow?
        </h1>
        <p className="mt-5 font-sans text-[16px] leading-[1.65] text-charcoal-900 max-w-[58ch]">
          Most families are dramatically underinsured. The DIME method —{" "}
          <em>Debt, Income, Mortgage, Education</em> — is how Strategists get to the
          real number in three short steps.
        </p>

        <Dots step={step} />

        <div className="mt-8 bg-white border border-bronze-600/25 rounded-[4px] p-7 shadow-soft">
          {step === 0 && <StepIntro onNext={onNext} />}
          {step === 1 && (
            <StepDebt d={d} setD={setD} onNext={onNext} onBack={onBack} />
          )}
          {step === 2 && (
            <StepIncome d={d} setD={setD} onNext={onNext} onBack={onBack} />
          )}
          {step === 3 && (
            <StepMortgageEdu d={d} setD={setD} onNext={onSubmit} onBack={onBack} />
          )}
        </div>

        {step === 0 && (
          <p className="mt-4 font-sans italic text-[12px] text-charcoal-500">
            Takes under two minutes. Nothing is shared with carriers.
          </p>
        )}
      </div>

      {/* Live accumulator sidebar */}
      <aside className="hidden lg:block">
        <div className="sticky top-24 bg-navy-900 text-cream-50 rounded-[4px] p-6 shadow-soft">
          <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-bronze-400">
            Building your number
          </p>

          <div className="mt-6 h-44 bg-navy-800/60 rounded-[2px] relative overflow-hidden">
            <div
              className="absolute bottom-0 left-0 right-0 transition-all duration-700 ease-out"
              style={{
                height: `${Math.round(filled * 100)}%`,
                background: "linear-gradient(180deg, var(--color-bronze-400), var(--color-bronze-600))",
              }}
            />
          </div>

          <div className="mt-6 space-y-3 font-sans text-[13px]">
            <Row label="Debt" value={step >= 1 ? fmtUSD(calc.debt) : "—"} on={step >= 1} />
            <Row label="Income × years" value={step >= 2 ? fmtUSD(calc.incomeReplace) : "—"} on={step >= 2} />
            <Row label="Mortgage" value={step >= 3 ? fmtUSD(calc.mortgage) : "—"} on={step >= 3} />
            <Row label="Education" value={step >= 3 ? fmtUSD(calc.education) : "—"} on={step >= 3} />
          </div>

          {step === 3 ? null : (
            <p className="mt-6 font-sans italic text-[11px] text-bronze-400/90 leading-relaxed">
              The full number appears when you finish.
            </p>
          )}
        </div>
      </aside>
    </div>
  );
}

function Row({ label, value, on }: { label: string; value: string; on: boolean }) {
  return (
    <div className="flex items-center justify-between border-b border-bronze-400/20 pb-2">
      <span className={on ? "text-cream-50" : "text-cream-50/40"}>{label}</span>
      <span className={on ? "text-bronze-400 font-semibold" : "text-cream-50/40"}>{value}</span>
    </div>
  );
}

function Dots({ step }: { step: number }) {
  return (
    <div className="mt-8 flex items-center gap-3">
      {[1, 2, 3].map((n) => (
        <span
          key={n}
          className={
            "h-1.5 w-10 rounded-full transition-colors " +
            (step >= n ? "bg-bronze-600" : "bg-bronze-600/20")
          }
        />
      ))}
      <span className="ml-3 font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">
        {step === 0 ? "Start" : `Step ${step} of 3`}
      </span>
    </div>
  );
}

function StepIntro({ onNext }: { onNext: () => void }) {
  return (
    <div>
      <h2 className="font-display text-[24px] text-navy-900">Begin with what your family already carries.</h2>
      <p className="mt-3 font-sans text-[14px] leading-relaxed text-charcoal-900 max-w-[52ch]">
        Three questions. We&rsquo;ll add Debt, then Income replacement, then
        Mortgage and Education. The total at the end is the coverage that keeps
        your family in their home and on their path.
      </p>
      <div className="mt-7">
        <PrimaryButton onClick={onNext} size="lg">Begin · Step 1 →</PrimaryButton>
      </div>
    </div>
  );
}

function MoneyField({
  label, hint, value, onChange, max,
}: { label: string; hint?: string; value: number; onChange: (n: number) => void; max?: number }) {
  return (
    <div>
      <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">
        {label}
      </Label>
      <div className="mt-2 relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 font-display text-navy-800">$</span>
        <Input
          type="number"
          min={0}
          max={max}
          value={value}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
          className="pl-7 font-display text-[18px] text-navy-900 h-12"
        />
      </div>
      {hint && <p className="mt-2 font-sans text-[12px] text-charcoal-500">{hint}</p>}
    </div>
  );
}

function StepDebt({
  d, setD, onNext, onBack,
}: { d: DimeState; setD: (d: DimeState) => void; onNext: () => void; onBack: () => void }) {
  return (
    <div>
      <Eyebrow>— Step 1 · Debt —</Eyebrow>
      <h2 className="mt-3 font-display text-[24px] text-navy-900">What would your family inherit, in obligations?</h2>
      <p className="mt-3 font-sans text-[14px] leading-relaxed text-charcoal-900 max-w-[52ch]">
        Credit cards, auto loans, student loans, personal loans, medical
        balances. Anything that does not go away when you do. (Mortgage is its
        own step.)
      </p>
      <div className="mt-6 max-w-md">
        <MoneyField label="Total non-mortgage debt" value={d.debt} onChange={(v) => setD({ ...d, debt: v })} />
      </div>
      <StepNav onBack={onBack} onNext={onNext} nextLabel="Step 2 · Income →" />
    </div>
  );
}

function StepIncome({
  d, setD, onNext, onBack,
}: { d: DimeState; setD: (d: DimeState) => void; onNext: () => void; onBack: () => void }) {
  return (
    <div>
      <Eyebrow>— Step 2 · Income —</Eyebrow>
      <h2 className="mt-3 font-display text-[24px] text-navy-900">What does your family need to live the same life?</h2>
      <p className="mt-3 font-sans text-[14px] leading-relaxed text-charcoal-900 max-w-[52ch]">
        Industry guidance is 10× annual income for a working-age earner.
        Adjust if you carry more, or fewer, dependents.
      </p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl">
        <MoneyField label="Annual gross income" value={d.income} onChange={(v) => setD({ ...d, income: v })} />
        <div>
          <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">
            Years to replace
          </Label>
          <div className="mt-2 flex items-center gap-3">
            <input
              type="range"
              min={5}
              max={20}
              value={d.incomeYears}
              onChange={(e) => setD({ ...d, incomeYears: Number(e.target.value) })}
              className="w-full accent-bronze-600"
            />
            <span className="font-display text-navy-900 text-[18px] min-w-[3ch] text-right">
              {d.incomeYears}×
            </span>
          </div>
          <p className="mt-2 font-sans text-[12px] text-charcoal-500">
            10 years is standard. Use 15–20 if you have young kids.
          </p>
        </div>
      </div>
      <StepNav onBack={onBack} onNext={onNext} nextLabel="Step 3 · Mortgage & Education →" />
    </div>
  );
}

function StepMortgageEdu({
  d, setD, onNext, onBack,
}: { d: DimeState; setD: (d: DimeState) => void; onNext: () => void; onBack: () => void }) {
  return (
    <div>
      <Eyebrow>— Step 3 · Mortgage & Education —</Eyebrow>
      <h2 className="mt-3 font-display text-[24px] text-navy-900">Keep them in the home. Keep them in school.</h2>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl">
        <MoneyField
          label="Remaining mortgage balance"
          value={d.mortgage}
          onChange={(v) => setD({ ...d, mortgage: v })}
        />
        <div>
          <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">
            Children to support
          </Label>
          <div className="mt-2 flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={6}
              value={d.kids}
              onChange={(e) => setD({ ...d, kids: Number(e.target.value) })}
              className="w-full accent-bronze-600"
            />
            <span className="font-display text-navy-900 text-[18px] min-w-[2ch] text-right">{d.kids}</span>
          </div>
        </div>
        <div className="sm:col-span-2">
          <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">
            Education plan per child
          </Label>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
            {(Object.keys(EDU_LABELS) as DimeState["educationLevel"][]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setD({ ...d, educationLevel: k })}
                className={
                  "text-left p-3 rounded-[2px] border transition-colors " +
                  (d.educationLevel === k
                    ? "border-bronze-600 bg-bronze-600/10"
                    : "border-bronze-600/25 bg-white hover:border-bronze-600/60")
                }
              >
                <span className="font-sans text-[12px] text-charcoal-900 capitalize block">
                  {k}
                </span>
                <span className="font-sans text-[11px] text-charcoal-500 block">
                  {EDU_LABELS[k]}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <StepNav onBack={onBack} onNext={onNext} nextLabel="See your number →" />
    </div>
  );
}

function StepNav({ onBack, onNext, nextLabel }: { onBack: () => void; onNext: () => void; nextLabel: string }) {
  return (
    <div className="mt-8 flex items-center justify-between">
      <button
        type="button"
        onClick={onBack}
        className="font-sans text-[12px] uppercase tracking-[0.18em] text-charcoal-500 hover:text-navy-800"
      >
        ← Back
      </button>
      <PrimaryButton onClick={onNext} size="lg">{nextLabel}</PrimaryButton>
    </div>
  );
}

/* ---------------- RESULT ---------------- */

function Result({
  d, calc, onRestart,
}: { d: DimeState; calc: ReturnType<typeof computeDime>; onRestart: () => void }) {
  const share = emailShareLink(
    "Our DIME coverage estimate",
    `Currency Thoughts DIME estimate:\n\nTotal coverage: ${fmtUSD(calc.total)}\n  Debt: ${fmtUSD(calc.debt)}\n  Income replacement: ${fmtUSD(calc.incomeReplace)} (${d.income} × ${d.incomeYears})\n  Mortgage: ${fmtUSD(calc.mortgage)}\n  Education: ${fmtUSD(calc.education)} (${d.kids} kids · ${d.educationLevel})\n\nSee the method: https://www.currencythoughts.net/tools/dime`,
  );

  return (
    <div className="mt-8">
      <div className="mx-auto max-w-3xl text-center">
        <Eyebrow align="center">— Your DIME Coverage Need —</Eyebrow>
        <h1
          className="mt-6 font-display leading-[1] text-[64px] md:text-[96px]"
          style={{
            backgroundImage: "linear-gradient(180deg, var(--color-bronze-400), var(--color-bronze-600))",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          {fmtUSD(calc.total)}
        </h1>
        <p className="mt-6 font-sans text-[15px] md:text-[16px] leading-[1.7] text-charcoal-900 max-w-[60ch] mx-auto">
          This is the coverage that protects everything you&rsquo;ve built. The
          good news: at your age and health, this likely costs less per month
          than you&rsquo;d expect — and a Strategist can show you the real number
          in one call.
        </p>
      </div>

      <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
        <Breakdown label="Debt" value={fmtUSD(calc.debt)} />
        <Breakdown label={`Income · ${d.incomeYears}×`} value={fmtUSD(calc.incomeReplace)} />
        <Breakdown label="Mortgage" value={fmtUSD(calc.mortgage)} />
        <Breakdown label="Education" value={fmtUSD(calc.education)} />
      </div>

      <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
        <PrimaryButton asChild size="lg">
          <a href="/book?source=dime">Get a Real Quote →</a>
        </PrimaryButton>
        <SecondaryButton asChild size="lg">
          <Link to="/tools/family-snapshot">Take the Full Family Snapshot →</Link>
        </SecondaryButton>
      </div>

      <div className="mt-10 max-w-3xl mx-auto text-center">
        <p className="font-sans text-[13px] text-charcoal-500">
          Want to understand the math?{" "}
          <Link
            to="/academy/lessons/$lessonSlug"
            params={{ lessonSlug: "how-much-coverage-dime" }}
            className="text-bronze-600 hover:text-bronze-400 underline underline-offset-4"
          >
            Read &ldquo;The DIME method&rdquo; in the Academy →
          </Link>
        </p>
        <div className="mt-5 flex items-center justify-center gap-3 text-[12px]">
          <a
            href={share}
            className="font-sans text-bronze-600 hover:text-bronze-400 underline underline-offset-4"
          >
            Email this to my spouse
          </a>
          <span className="text-charcoal-500">·</span>
          <button
            onClick={onRestart}
            className="font-sans text-charcoal-500 hover:text-navy-800 underline underline-offset-4"
          >
            Recalculate
          </button>
        </div>
      </div>

      <div className="mt-12 max-w-3xl mx-auto">
        <CalculatorDisclaimer />
      </div>
    </div>
  );
}

function Breakdown({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white border border-bronze-600/20 rounded-[4px] p-5 text-center shadow-soft">
      <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-charcoal-500">{label}</p>
      <p className="mt-3 font-display text-[22px] text-navy-900">{value}</p>
    </div>
  );
}